import {observer_mgl} from "mobx-graphlink";
import React from "react";
import {Button, CheckBox, Row, RowLR, Select, Spinner, Text, TextArea} from "react-vcomponents";
import {ShowMessageBox} from "react-vmessagebox";
import {TextPlus} from "web-vcore";
import {Interval, PathTerrainFeature} from "../../../../../../Terrain/Heights/IsolineTypes.js";
import {GetMapObjects} from "../../../../../../Server/DB/mapObjects.js";
import {ResolveObjPosInWorld} from "../../../../../../Server/DB/mapObjects/$mapObject.js";
import {ObjPath} from "../../../../../../Server/DB/mapObjects/@MapObject/ObjPath.js";
import {ShowTerrainExportDialog} from "../../@Shared/AreaExportDialog.js";
import {PathEditorUI_SharedProps} from "../PathEditorUI.js";

export const PathEditorTab_Terrain = observer_mgl((props: {} & PathEditorUI_SharedProps)=>{
	const {map, obj, path, index, Change} = props;
	const objPos = ResolveObjPosInWorld(obj);
	const allMapObjects = GetMapObjects(map.id);
	const pathIdDuplicate = !!path.path_id && (allMapObjects ?? []).some(obj2=>{
		return (obj2.paths ?? []).some(path2=>{
			if (obj2.id === obj.id && obj2.paths!.indexOf(path2) === index) return false; // skip self
			return path2.path_id === path.path_id;
		});
	});

	return <>
		<Row>
			<CheckBox text="Terrain feature (V3 Poisson)" value={path.terrainFeature != null && !path.terrainFeature.disabled} onChange={val=>{
				Change(a=>{
					if (val) {
						// First enable: create the struct if it doesn't exist yet (preserving the
						// "if they're trying to enable and the struct doesn't exist yet, THEN add" rule).
						// Subsequent toggles clear `disabled` (set to undefined) rather than adding/removing
						// the struct, so the rest of the config (pin-shape / elevation / soft-pin / noFlux /
						// etc.) is preserved across disable→re-enable.
						a.terrainFeature ??= new PathTerrainFeature();
						a.terrainFeature.disabled = undefined;
						// Auto-generate a stable path_id on first enable (used for deterministic RNG seeding).
						if (!a.path_id) a.path_id = ObjPath.NewPathID();
					} else {
						// Disable in-place (keep the struct + its config; consumer IsActive() now returns false).
						if (a.terrainFeature) a.terrainFeature.disabled = true;
					}
				});
			}}/>
			{path.terrainFeature != null &&
				<Button ml={5} mdIcon="delete" title="Delete terrain-feature data (asks for confirmation)" onClick={()=>{
					ShowMessageBox({
						title: "Delete terrain feature?",
						cancelButton: true,
						message: `
							Permanently delete this path's terrain-feature data?
							This removes the whole configuration (pin shape, elevation, soft-pin settings, no-flux, etc.)
						`.AsMultiline(0),
						onOK: ()=>{
							Change(a=>{
								a.terrainFeature = undefined;
							});
						},
					});
				}}/>}
		</Row>
		{path.terrainFeature && <>
			<Row mt={5}>
				<TextPlus style={{whiteSpace: "pre"}} info={`
					Whether this feature pins an elevation, and on which cells (the V3 pin-shape axis; Sec 5.3.2).
					* None      -> no elevation pin (a non-terrain path, or a barrier-only cliff when paired with no-flux below).
					* Interior  -> pin all interior + rim cells at the resolved elevation (a flat plateau, lake basin, peak rim).
									Pair with a closed path (see the curve's Closed checkbox above) for a wrapped ring.
					* Rim       -> pin only the cells the polyline passes through; the interior solves freely to a smooth
									harmonic field (a mountainside between this rim and any inner peak's rim). On a closed path
									this is a closed mountain-ring ridge; on an open path it's a river/road/ridgeline.
					Closed-vs-open path topology is read from the path's own "curve_closed" flag (above), not from
					this pin-shape selector — toggle the curve's Closed checkbox to switch between a mountain-ring and a river.
				`.AsMultiline(0)}>Pin shape:</TextPlus>
				<Select ml={5} displayType="button bar"
					options={[
						{name: "None", value: "none"},
						{name: "Interior (plateau)", value: "interior"},
						{name: "Rim (mountainside)", value: "rim"},
					]}
					value={path.terrainFeature.pinShape ?? "none"}
					onChange={val=>Change(a=>{
						a.terrainFeature ??= new PathTerrainFeature();
						a.terrainFeature.pinShape = val;
					})}/>
			</Row>
			{path.terrainFeature.pinShape && path.terrainFeature.pinShape !== "none" && <>
				<Row mt={5}>
					<TextPlus info={`
						How hard the pin is (orthogonal to Pin shape; the V3 pin-type axis). Composes with the Pin shape above.
						* Hard  -> Dirichlet CLAMP (the classic pin: cells are held AT the resolved elevation exactly;
									C0 value continuity, gradient free to jump across the pinned ring).
						* Soft  -> penalty spring k*(H - target) (the pin cells are PULLED toward the resolved elevation
									WITHOUT clamping it, so the SLOPE stays CONTINUOUS across the pinned ring — no kink).
									Stiffness k is the knob below -- k large approximates a hard pin; k small is a gentle guider.
									Useful for value-guided isolines where you want the field to pass near a target elevation
									at the isoline but flow smoothly both inside and out.
									Band direction depends on Pin shape: Rim soft ramps the band BOTH inward + outward (both
									sides are Free); Interior soft ramps OUTWARD ONLY (the interior cells keep uniform k — a
									soft plateau/lake basin; an in-ramp would lower the inner stiffness toward zero, fighting
									the plateau intent). On an OPEN path the band ramps symmetrically on both sides of the
									polyline (an open path has no inside/outside, so both sides are Free and the band ramps
									"both" — the open-tube analogue of the closed-path Rim soft band).
					`.AsMultiline(0)}>Pin type:</TextPlus>
					<Select ml={5} displayType="button bar"
						options={[
							{name: "Hard (clamp)", value: "hard"},
							{name: "Soft (continuous)", value: "soft"},
						]}
						value={path.terrainFeature.pinType ?? "hard"}
						onChange={val=>Change(a=>{
							a.terrainFeature ??= new PathTerrainFeature();
							a.terrainFeature.pinType = val;
						})}/>
				</Row>
				{path.terrainFeature.pinType === "soft" && <>
					<Row mt={5}>
						<TextPlus style={{fontWeight: "bold"}} info={`
							Soft-pin stiffness k (penalty term k*(H - target) pulls the pin cells toward the resolved
							elevation without clamping). Larger k = stronger pull (k large recovers a hard pin; k -> 0 =
							pure Free, no pull). The rasterizer scales k by 1/d^2 so a value of 1.0 means "penalty
							comparable to one Laplacian neighbour" at any cell size. Typical range 0.1-10.
							Works on both closed and open paths. Ignored unless Pin type = Soft.
						`.AsMultiline(0)}>Soft-pin stiffness (k):</TextPlus>
						<Spinner ml={5} style={{width: 80}} step="any" min={0} value={path.terrainFeature.softPinStiffness ?? 1}
							onChange={val=>Change(a=>{ a.terrainFeature!.softPinStiffness = val != null && val > 0 ? val : 1; })}/>
						<Button mdIcon="delete" ml={5} title="Reset to default (1.0)" onClick={()=>Change(a=>{ if (a.terrainFeature) a.terrainFeature.softPinStiffness = undefined; })}/>
					</Row>
					<Row mt={5}>
						<TextPlus style={{fontWeight: "bold"}} info={`
							Soft-pin band width W (m). When > 0, the penalty is spread across a band of cells of width W
							around the contour instead of the 1-cell pin. Per-cell stiffness ramps as k*cos(pi*d/(2W))
							(1 at the pin, 0 at the band edge) so the gradient transition is C1-ish (no visible seam).
							Wider band = smoother transition but more diffuse pull. 0 = 1-cell pin behaviour (backward
							compat; the seam-prone default). Typical range 0-200m. Ignored unless Pin type = Soft.

							Band direction: Rim soft ramps BOTH inward + outward (both sides are Free and need the smooth
							transition); Interior soft ramps OUTWARD ONLY (the interior cells keep uniform k — an in-ramp
							would lower the inner stiffness toward zero and fight the plateau intent). On an OPEN path the
							band ramps symmetrically on both sides of the polyline (an open path has no inside/outside,
							so both sides are Free and ramp "both" — the open-tube analogue of closed-path Rim soft).
						`.AsMultiline(0)}>Soft-pin band width (m):</TextPlus>
						<Spinner ml={5} style={{width: 80}} step="any" min={0} value={path.terrainFeature.softPinBandWidth ?? 0}
							onChange={val=>Change(a=>{ a.terrainFeature!.softPinBandWidth = val != null && val > 0 ? val : 0; })}/>
						<Button mdIcon="delete" ml={5} title="Reset to default (0 = 1-cell pin)" onClick={()=>Change(a=>{ if (a.terrainFeature) a.terrainFeature.softPinBandWidth = undefined; })}/>
					</Row>
				</>}
			</>}
			{path.terrainFeature.pinShape && path.terrainFeature.pinShape !== "none" && <>
				<Row mt={5}>
					<TextPlus style={{fontWeight: "bold"}} info={`
						Pin dominance (precedence among overlapping value pins). Larger value wins: when a later
						feature's pin would land on a cell an earlier feature has already pinned, the later write is
						BLOCKED iff the earlier cell's dominance is STRICTLY GREATER than this feature's dominance.
						Equal or lower = no block (later-writer-wins preserved). Default 0 = never blocks and is never
						blocked by another default-0 feature -> existing maps behave exactly as before.
						Set the OUTER (coastline) ring's dominance higher than the inner rings to protect it from the
						inner rings' padded rim sweeps reaching outward at coarse cell sizes (the lake "coastline
						floods out on zoom-out" leak). Setting only the outer ring's dominance suffices (the inner
						rings keep default 0 and are blocked by it).
					`.AsMultiline(0)}>Dominance:</TextPlus>
					<Spinner ml={5} style={{width: 80}} step="any" value={path.terrainFeature.dominance}
						onChange={val=>Change(a=>{ a.terrainFeature!.dominance = val != null && val !== 0 ? val : undefined; })}/>
					<Button mdIcon="delete" ml={5} title="Reset to default (0 = no blocking)" onClick={()=>Change(a=>{ if (a.terrainFeature) a.terrainFeature.dominance = undefined; })}/>
				</Row>
				<Row mt={5}>
					<TextPlus style={{fontWeight: "bold"}} info={`
						Fallback pin. When on, and this feature's pin is blocked by a higher-dominance cell, the pin
						reroutes to the closest unblocked leaf INSIDE this feature's own polygon (closed rings) /
						within the refined tube (open curves) / within the disk radius (spot heights) and stamps
						there instead. Keeps an inner ring's pin "alive" when an outer higher-dominance ring's
						padded sweep would otherwise erase it -> the inner pin relocates a cell or two inward
						(staying inside its true footprint) rather than vanishing. Default off (a blocked pin is
						silently dropped). Only meaningful when dominance is set AND lower than some other
						feature's dominance.
					`.AsMultiline(0)}>Fallback pin:</TextPlus>
					<CheckBox value={!!path.terrainFeature.fallbackPin === true} onChange={val=>Change(a=>{ a.terrainFeature!.fallbackPin = val ? true : undefined; })}/>
				</Row>
				<Row mt={5}>
					<TextPlus style={{fontWeight: "bold"}} info={`
						Mute dominance when. When set, the terrain system builds a *second* macro-grid variant with this isoline's dominance disabled (so inner rings can pull the macro H on shared macro cells instead of being blocked), used only when the (padded) camera viewport meets the gate condition:
						* "Camera fully inside" -> fires when the padded viewport is entirely INSIDE this isoline's polygon. Use for a lake coastline: when the user zooms fully inside the lake, the coast's dominance is muted so the inner water rings pull the macro correctly (fixes the "sand in water on close-zoom" bug). The coast's dominance still protects it at far-zoom / when straddling.
						* "Camera fully outside" -> fires when the padded viewport is entirely OUTSIDE this isoline's polygon. Use for an island's coastline inside a lake: when the camera is in the surrounding lake (outside the island), the island's dominance is muted so the lake's inner rings pull the macro smoothly across the island coast.
						The "padded" margin is controlled by the terrain-definition's "Preserve-dominance padding" setting (default 10% of the larger view dimension), so the mute fires clearly inside/outside rather than flapping at the exact coastline. Default "Never" (the dominance stays in effect everywhere — backward compat). Only meaningful when dominance is set (> 0).
					`.AsMultiline(0)}>Mute dominance when:</TextPlus>
					<Select ml={5} style={{width: 180}} displayType="button bar" options={[
						{name: "Never", value: "never"},
						{name: "Camera fully inside", value: "paddedViewportFullyInside"},
						{name: "Camera fully outside", value: "paddedViewportFullyOutside"},
					]} value={path.terrainFeature.muteDominanceWhen ?? "never"} onChange={val=>Change(a=>{ a.terrainFeature!.muteDominanceWhen = val === "never" ? undefined : val as any; })}/>
				</Row>
			</>}
			{path.curve_closed === true && <>
				<Row mt={5}>
					<TextPlus style={{fontWeight: "bold"}} info={`
						Ambient-noise application multiplier for the region INSIDE this isoline (only meaningful on a
						closed path). When set, every sample point inside the isoline's polygon has its ambient-noise
						contribution (the L base-relief band + the D detail band scaled by the detail-damping weight) multiplied by this value. Default unset (1 = full ambient, no change to existing maps).

						Practical example: set Ambient weight multiplier = 0 inside a lake's coastline isoline to block
						ambient-noise from creating its own islands inside the lake floor (the lake's resolved H still
						gets its pin-driven harmonic base — the multiplier only mutes the add-on-top ambient term, NOT
						the Poisson-solved elevation).

						The INNERMOST containing closed ring wins for a point: a peak-island ring inside a zero-ambient
						lake governs its own interior's multiplier, so an explicitly pinned island can carry full ambient
						while the surrounding lake floor is silenced — the multiplier composes with nesting topology, not
						with dominance or the pin shape.

						Purely an eval-time ambient-term modulator — does NOT enter the Poisson solve and does NOT retrigger
						a macro/working grid rebuild when changed. Leave unset on open paths (an open path has no
						"inside").
					`.AsMultiline(0)}>Ambient weight multiplier (inside this closed isoline):</TextPlus>
					<Spinner ml={5} style={{width: 90}} step="any" min={0} value={path.terrainFeature.ambientWeightMultiplier}
						onChange={val=>Change(a=>a.terrainFeature!.ambientWeightMultiplier = val != null ? val : undefined)}/>
					<Button mdIcon="delete" ml={5} title="Reset to default (1 = full ambient, no change)" onClick={()=>Change(a=>{ if (a.terrainFeature) a.terrainFeature.ambientWeightMultiplier = undefined; })}/>
				</Row>
			</>}
			<RowLR mt={5} splitAt={160}>
				<TextPlus info={`
					If true, cells the polyline passes through become Barrier cells (excluded from the Poisson solve —
					the field on each side of the barrier is computed independently). Independent of pin shape:
					* None  + no-flux    -> a pure flux wall (a cliff with no-flux).
					* Interior + no-flux -> a walled plateau (barrier rim + flat interior pin).
					* Rim + no-flux      -> a barrier ridge with the elevation pinned on it (rare but composes).
					If false (default), no barrier is written; the elevation pin (if any) acts as a regular Dirichlet.
				`.AsMultiline(0)}>No flux (ie. barrier):</TextPlus>
				<CheckBox value={!!path.terrainFeature.noFlux === true} onChange={val=>Change(a=>{ a.terrainFeature!.noFlux = val ? true : undefined; })}/>
			</RowLR>
			{path.terrainFeature.pinShape !== "none" && <>
				<Row mt={5}>
					<TextPlus style={{fontWeight: "bold"}} info={`
						Pinned elevation (meters above sea level). The Interval's resolved scalar (midpoint, or one
						bound if the other is absent) is the FALLBACK for any vertex whose per-vertex elevation below
						is null. For pin shape "Interior", every interior + rim cell pins at this resolved scalar
						(unless per-vertex overrides are set on the rim). For pin shape "Rim", each rim cell pins at
						the per-vertex elevation of its nearest vertex (or the fallback if that entry is null).
					`.AsMultiline(0)}>Elevation constraint (m) — fallback for unset per-vertex entries:</TextPlus>
				</Row>
				<Row mt={3}>
					<Text>Min:</Text>
					<Spinner ml={5} style={{width: 80}} min={-Infinity}
						//validator={()=>path.terrainFeature?.elevation?.min != null ? true : "(red/-1 here means unset)"}
						value={path.terrainFeature.elevation?.min}
						onChange={val=>Change(a=>{
							a.terrainFeature!.elevation ??= new Interval();
							a.terrainFeature!.elevation.min = val ?? undefined;
						})}/>
					<Button mdIcon="delete" title="Clear min" onClick={()=>Change(a=>{ if (a.terrainFeature?.elevation) a.terrainFeature.elevation.min = undefined; })}/>
					<Text ml={10}>Max:</Text>
					<Spinner ml={5} style={{width: 80}} min={-Infinity}
						value={path.terrainFeature.elevation?.max}
						onChange={val=>Change(a=>{
							a.terrainFeature!.elevation ??= new Interval();
							a.terrainFeature!.elevation.max = val ?? undefined;
						})}/>
					<Button mdIcon="delete" title="Clear max" onClick={()=>Change(a=>{ if (a.terrainFeature?.elevation) a.terrainFeature.elevation.max = undefined; })}/>
				</Row>
				<Row mt={5}>
					<TextPlus style={{fontWeight: "bold"}} info={`
						Per-vertex elevation overrides (meters above sea level) for each control point of this path.
						Use null for any entry to fall back to the elevation Interval's resolved scalar above
						(so you can pin a few control points explicitly and leave the rest at the regional default).
						Length should match path.points.length; missing entirely = use the Interval for every vertex.
						The rasterizer interpolates linearly along the tessellated Catmull-Rom backbone between vertices.
					`.AsMultiline(0)}>Per-vertex elevations (m; null = use Interval fallback):</TextPlus>
				</Row>
				<Row mt={3}>
					<TextArea autoSize={true} style={{width: "100%"}} value={JSON.stringify(path.terrainFeature.perVertexElevations ?? [])}
						onChange={val=>Change(a=>{
							try {
								const parsed = JSON.parse(val);
								if (Array.isArray(parsed)) {
									a.terrainFeature!.perVertexElevations = parsed.map((v: any)=> (typeof v === "number") ? v : null);
								}
							} catch {/* ignore malformed */}
						})}/>
				</Row>
				<Row mt={3} style={{opacity: 0.6, fontSize: 11}}>
					<Text>{path.points.length} control points; {(path.terrainFeature.perVertexElevations ?? []).length} elevation entries.</Text>
				</Row>
				<Row mt={5}>
					<TextPlus info={`
						Which side of the isoline is uphill (drives the Neumann rim-face sign). Interior up (default):
						the inside of the ring is the high side (a peak / plateau / mountain-ring ridge). Interior down:
						the inside is the low side (a basin / crater / circular valley ridge). For an open path, the
						two side-faces share the sign convention (valley walls climb away symmetrically).
						Set crossingSlope to make the wall rise/fall at a specific grade as you cross the contour.
					`.AsMultiline(0)}>Orientation:</TextPlus>
					<Select ml={5} displayType="button bar"
						options={[{name: "(auto / interior up)", value: null}, {name: "Interior up", value: "interiorUp"}, {name: "Interior down", value: "interiorDown"}]}
						value={path.terrainFeature.orientation ?? null}
						onChange={val=>Change(a=>{ a.terrainFeature!.orientation = val ?? undefined; })}/>
				</Row>
				<Row mt={5}>
					<TextPlus info={`
						Steepness (meters of rise per meter of horizontal run) crossing this contour — the Neumann
						slope MAGNITUDE. V3 maps this to a Neumann BC on the rim/outward faces of the constraint
						cells (when the terrain definition's poissonSolver.enableNeumann is true). The sign is
						supplied by Orientation above; crossingSlope only sets the magnitude. Set min=max for an
						exact slope; leave both unset for no Neumann pin (pure Dirichlet).
					`.AsMultiline(0)}>Crossing slope (m/m):</TextPlus>
				</Row>
				<Row mt={3}>
					<Text>Min:</Text>
					<Spinner ml={5} style={{width: 80}} step={0.01} min={0}
						value={path.terrainFeature.crossingSlope?.min ?? null}
						onChange={val=>Change(a=>{
							if (a.terrainFeature!.crossingSlope == null) a.terrainFeature!.crossingSlope = new Interval();
							a.terrainFeature!.crossingSlope.min = val ?? undefined;
						})}/>
					<Button mdIcon="delete" title="Clear min" onClick={()=>Change(a=>{
						const cs = a.terrainFeature?.crossingSlope;
						if (cs) cs.min = undefined;
					})}/>
					<Text ml={10}>Max:</Text>
					<Spinner ml={5} style={{width: 80}} step={0.01} min={0}
						value={path.terrainFeature.crossingSlope?.max ?? null}
						onChange={val=>Change(a=>{
							if (a.terrainFeature!.crossingSlope == null) a.terrainFeature!.crossingSlope = new Interval();
							a.terrainFeature!.crossingSlope.max = val ?? undefined;
						})}/>
					<Button mdIcon="delete" title="Clear max" onClick={()=>Change(a=>{
						const cs = a.terrainFeature?.crossingSlope;
						if (cs) cs.max = undefined;
					})}/>
				</Row>
			</>}
			<RowLR mt={5} splitAt={160}>
				<TextPlus info={`
					Target solver cell size (meters) along this feature. Default: the terrain definition's
					poissonSolver.defaultConstraintCellSize. Smaller = sharper curve capture (and more memory);
					larger = coarser but cheaper.
				`.AsMultiline(0)}>Cell size (m):</TextPlus>
				<Row>
					<Spinner step="any" min={0.1} value={path.terrainFeature.cellSize}
						onChange={val=>Change(a=>{ a.terrainFeature!.cellSize = val && val > 0 ? val : undefined; })}/>
					<Button mdIcon="delete" ml={5} title="Clear (use terrain default)" onClick={()=>Change(a=>{ if (a.terrainFeature) a.terrainFeature.cellSize = undefined; })}/>
				</Row>
			</RowLR>
			{path.terrainFeature.pinShape !== "none" && <>
				<RowLR mt={5} splitAt={160}>
					<TextPlus info={`
						Per-region diffusion-coefficient override on the pinned cells (the c in div(c grad H) = f).
						Default 1 (the global c). Lowering c makes the wall to neighbouring terrain steeper;
						raising c makes it gentler. For pin shape "Interior" this applies to the interior + rim
						cells; for pin shape "Rim" it applies to the rim cells only.
					`.AsMultiline(0)}>Region c (diffusion):</TextPlus>
					<Row>
						<Spinner step="any" min={0} value={path.terrainFeature.regionC}
							onChange={val=>Change(a=>{ a.terrainFeature!.regionC = val && val > 0 ? val : undefined; })}/>
						<Button mdIcon="delete" ml={5} title="Clear (use global c)" onClick={()=>Change(a=>{ if (a.terrainFeature) a.terrainFeature.regionC = undefined; })}/>
					</Row>
				</RowLR>
				<RowLR mt={5} splitAt={160}>
					<TextPlus info={`
						Per-region Poisson source override on the pinned cells (the f in -div(c grad H) = f).
						Default 0 (Laplace). Positive = gentle upward bulge; negative = bowl.
					`.AsMultiline(0)}>Region f (source):</TextPlus>
					<Row>
						<Spinner step="any" value={path.terrainFeature.regionF}
							onChange={val=>Change(a=>{ a.terrainFeature!.regionF = val !== 0 ? val : undefined; })}/>
						<Button mdIcon="delete" ml={5} title="Clear (Laplace)" onClick={()=>Change(a=>{ if (a.terrainFeature) a.terrainFeature.regionF = undefined; })}/>
					</Row>
				</RowLR>
			</>}
			<Row mt={8}>
				<Button text="Export area for unit-tests" onClick={()=>{
					ShowTerrainExportDialog(obj, path, allMapObjects, map.id);
				}}/>
			</Row>
		</>}
	</>;
});